import type { ComponentProps, ComponentType, ReactNode, RefObject } from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react'
import type { PageInfo } from 'src/services/apollo/generated/graphql'
import type { Connection, ConnectionNode } from 'src/utils/graphql'
import { getNodesFromConnection } from 'src/utils/graphql'
import LoadingPage from '../LoadingPage'
import type { BaseCursorPagingButtonProps } from './CursorPagingButton'
import CursorPagingButton from './CursorPagingButton'
import { getBaseCursorPagingArgs } from './cursorPagingUtils'

export interface BaseCursorPagingContextType {
  mutate: () => void
  data?: any
}

const BaseCursorPagingContext = createContext<BaseCursorPagingContextType>({
  mutate: async () => {},
})

export const useBaseCursorPagingContext = () =>
  useContext(BaseCursorPagingContext)

interface SWRLikeResponse<Data> {
  data: Data
  isValidating: boolean
  mutate: () => Promise<any>
  error?: any
}

interface ApolloLikeResponse<Data> {
  data: Data
  loading: boolean
  refetch: () => Promise<any>
  error?: any
}

type SWRResponseData<T> = T extends { data: infer R } ? R : never

export type BaseCursorPagingRef = BaseCursorPagingContextType

const BaseCursorPaging = <
  UseSWR extends (arg?: any) => SWRLikeResponse<any> | ApolloLikeResponse<any>,
  GetConnection extends (
    result?: Result,
  ) => (Connection<any> & { pageInfo?: PageInfo }) | undefined,
  Item = ConnectionNode<NonNullable<ReturnType<GetConnection>>>,
  RenderItem extends (item: any, index: number) => ReactNode = (
    item: Item,
    index: number,
  ) => ReactNode,
  Result = SWRResponseData<ReturnType<UseSWR>>,
  Args = Parameters<UseSWR>[0],
>(props: {
  before?: string
  after?: string
  onCursorChange?: (cursor: Record<string, any>) => any
  useSWR: UseSWR
  getConnection: GetConnection
  extraArgs?: Partial<Args>
  isPosSeq?: boolean
  pageSize: number
  renderContainer?: (props: { children: ReactNode }) => ReactNode
  renderItem: RenderItem
  PagingButton?: ComponentType<BaseCursorPagingButtonProps>
  loading?: ReactNode
  empty?: ReactNode
  renderError?: (error: unknown) => ReactNode
  pagingRef?: RefObject<BaseCursorPagingRef | null>
  onDataChange?: (data?: Result) => any
  /** Use hash params instead of query params for cursor */
  preferHashParams?: boolean
}) => {
  const {
    before,
    after,
    onCursorChange,
    useSWR,
    getConnection,
    extraArgs,
    isPosSeq,
    pageSize,
    renderContainer = ({ children }) => (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    ),
    renderItem,
    PagingButton = CursorPagingButton,
    loading = <LoadingPage />,
    empty,
    renderError,
    pagingRef,
    onDataChange = () => {},
    preferHashParams = false,
  } = props

  const hookResult = useSWR({
    ...getBaseCursorPagingArgs({
      before,
      after,
      pageSize,
      isPosSeq,
    }),
    ...extraArgs,
  })
  const data = hookResult.data
  const mutate = 'mutate' in hookResult ? hookResult.mutate : hookResult.refetch
  const isValidating =
    'isValidating' in hookResult ? hookResult.isValidating : hookResult.loading
  const error = hookResult.error

  useEffect(() => {
    onDataChange(data)
  }, [data])

  const connection = getConnection(data)

  const items = useMemo(() => {
    const items = getNodesFromConnection(connection)
    if (isPosSeq) return items
    return items.reverse()
  }, [data])

  const ctxValue = useMemo(() => {
    return {
      mutate,
      data,
    }
  }, [mutate, data])

  useImperativeHandle(pagingRef, () => {
    return ctxValue
  }, [ctxValue])

  const handleCursorChange = ({
    before,
    after,
  }: {
    before?: string
    after?: string
  }) => {
    const newCursorData = { before, after, ...extraArgs }
    onCursorChange?.(newCursorData)
  }

  return (
    <BaseCursorPagingContext.Provider value={ctxValue}>
      {items.length > 0 ? (
        <>
          {renderContainer({
            children: items.map(renderItem),
          })}
          <PagingButton
            pageInfo={connection?.pageInfo}
            onChange={handleCursorChange}
            isPosSeq={isPosSeq}
            preferHashParams={preferHashParams}
          />
        </>
      ) : isValidating ? (
        loading
      ) : (
        (error && renderError?.(error)) || empty
      )}
    </BaseCursorPagingContext.Provider>
  )
}

export default BaseCursorPaging

export type BaseCursorPagingProps = ComponentProps<typeof BaseCursorPaging>
